import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/45b021a8/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`IBM’s pictograms are visual symbols used to represent ideas, objects, or
narratives. They can communicate messages at a glance, afford interactivity,
and simplify complex ideas. They draw from details found in the Plex typeface
and work well in presentations and marketing communications.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Design and production guidelines</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Producing an pictogram</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Exporting SVGs</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Contribution process</AnchorLink>
    </AnchorLinks>
    <h2>{`Design and production guidelines`}</h2>
    <p>{`Don’t see the pictogram you need in the library? Make your own! Follow these guidelines to ensure visual consistency and proper formatting.`}</p>
    <ul>
      <li parentName="ul">{`All pictograms should be unique and not redundant with any existing pictograms in the system. Look through the pictograms in the `}<a parentName="li" {...{
          "href": "sketch://add-library/cloud/75VZZ"
        }}>{`IBM Design Sketch kit`}</a>{` to ensure that your proposed new pictogram is not already represented.`}</li>
      <li parentName="ul">{`All pictograms should adhere to the `}<a parentName="li" {...{
          "href": "https://www.ibm.com/design/language/elements/pictograms/design"
        }}>{`IBM Design Language visual style`}</a>{`.`}</li>
      <li parentName="ul">{`All pictograms should comply with IBM `}<a parentName="li" {...{
          "href": "/guidelines/accessibility/overview"
        }}>{`accessibility standards`}</a>{`.`}</li>
      <li parentName="ul">{`All pictograms should be usable across all supported platforms and devices.`}</li>
      <li parentName="ul">{`All pictograms should be understandable by a global audience of users, regardless of nationality or language.`}</li>
    </ul>
    <h2>{`Producing an icon`}</h2>
    <ul>
      <li parentName="ul">{`Create a 32 x 32 px artboard for each pictogram.`}</li>
      <li parentName="ul">{`32 px pictograms should have 1 px padding.`}</li>
      <li parentName="ul">{`Set your workspace up from the start to snap to pixels.`}</li>
      <li parentName="ul">{`Be aware of automatic alignments which can place vectors on uneven or half pixels.`}</li>
      <li parentName="ul">{`Ungroup pictogram layers completely. The pictogram should be on the top-most layer in your artboard.`}</li>
      <li parentName="ul">{`Make sure to properly name layers and artboards (these names will also be exported into the code).`}</li>
    </ul>
    <h3>{`Production-ready`}</h3>
    <p>{`To be considered production-ready, all pictogram submissions must be delivered in SVG format at 32 x 32 px.`}</p>
    <ul>
      <li parentName="ul">{`Each artboard and the artwork within it must be aligned to the pixel grid.`}</li>
      <li parentName="ul">{`All strokes must be expanded and at full pixel values.`}</li>
      <li parentName="ul">{`All possible shapes and paths should be combined.`}</li>
    </ul>
    <h2>{`Exporting SVGs`}</h2>
    <h3>{`Export SVGs from Sketch`}</h3>
    <ol>
      <li parentName="ol">{`Draw a 32 x 32 px artboard. 32 px pictograms should have 1 px padding.`}</li>
      <li parentName="ol">{`Place the pictogram squarely on the artboard, making sure it’s aligned to the pixel grid.`}</li>
      <li parentName="ol">{`Convert all strokes to outlines(Shift + ⌘ + O)`}</li>
      <li parentName="ol">{`Select any overlapping shapes and click the Union icon from the top navigation to merge all of the shapes together.`}</li>
      <li parentName="ol">{`Make sure the icon is at #000000 and has no additional styling.`}</li>
      <li parentName="ol">{`Select the entire artboard (not just the icon).`}</li>
      <li parentName="ol">{`Click Make Exportable at the bottom of the right toolbar in Sketch.`}</li>
      <li parentName="ol">{`In the Export widget select “SVG” in the format dropdown.`}</li>
      <li parentName="ol">{`Click Export ‘Artboard-Name’.`}</li>
      <li parentName="ol">{`Name icon with the #name—modifier convention. (ex. copy.svg, copy—glyph.svg, add.svg, add—glyph.svg).`}</li>
    </ol>
    <h3>{`Export SVGs from Adobe Illustrator`}</h3>
    <ol>
      <li parentName="ol">{`Draw a 32 x 32 px artboard. 32 px pictograms should have 1 px padding.`}</li>
      <li parentName="ol">{`Place the icon squarely on the artboard, making sure it’s aligned to the pixel grid.`}</li>
      <li parentName="ol">{`Expand all strokes (Object > Expand).`}</li>
      <li parentName="ol">{`Select all overlapping shapes and click the “Unite” icon in the Pathfinder panel to merge all of the shapes together.`}</li>
      <li parentName="ol">{`Make sure the icon is at #000000 and has no additional styling.`}</li>
      <li parentName="ol">{`Select File > Save a Copy… from the top navigation.`}</li>
      <li parentName="ol">{`On the Format dropdown select “SVG.”`}</li>
      <li parentName="ol">{`Below Format select Use Artboard, then select either all or a range of artboards, depending on your need.`}</li>
      <li parentName="ol">{`Click Save.`}</li>
      <li parentName="ol">{`The SVG Options dialog will then open.`}</li>
      <li parentName="ol">{`Make sure the preferences are the same as in the image below.`}</li>
      <li parentName="ol">{`SVG export options image`}</li>
    </ol>
    <Row mdxType="Row">
      <Column colMd={6} colLg={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "134.2857142857143%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAbCAYAAAB836/YAAAACXBIWXMAABYlAAAWJQFJUiTwAAAC/UlEQVRIx32Vu24TURCG/S6hiBXb61tsr+O7Hd+iKIEKIZpIFLSUKIoEEhKKEl+oqHgfChoeiDOcb9azWq/XKX7N7Dmzs/P/Z+ZsbrPa/NusN7J+XrvV80rWq7UCf7vZytPjkzzcP8j953t5/P6oa/4d4Z0EnNrVxuVKpZJ4uFqtJufn54IF9XpdisWiFAoF9iUoBxIEQeQHQRYcNletVl273ZbRaOQGg4F0Oh1Fr9cTv6dJCcR/Ac4sCfXlRqOhL1YqFQ3Cpn1QLpcz1w05CyJZs9mUbrergH4+n5ezs7MY0KdiYEkNewmtyvF4LP1+X4EPJh5eDvXDMNTE6JhOaEk1IQcAZSywg7HDMZ+XYGO0MykTREVUaMEvvZRFc19DvzEcDiN4aiRGQ/REw/RHVO/EBzMppxdpo9lsFms4mUzUn06n3k7Uzudz1bTiEx8cCidqQMvINrVK0Gq1YttqhZqIZ/Q9qiFNjYU6wbRRTHHnp6fjaB+imbUGPjpCC6rD4SAewaTlA3byB5TToodhWzUi4eXlZQyq50NIYknT06UJLy4upNuJJgSftSQ1XrRmp3pi+t4mZ94Sa0JEJgi026Fu7G6hGBwW+1jiSUQ3gLqnHlPmpGaeHlSgBc2RpwZldF1eXalN0iOxaTz17UUsFev1RcLlcqn6ELBYLGToT5ygSLdRPNeRfnWVhv35rpADDSl/Pl9oM9M+bCbn2nzAnl3G9nzQh+iCFmhDcNa4HUNGY9dUI0B10c1S37tpkhXa2tHbhk2OX1tnd7la+/AB5LBJwrffxAsXrH/wVRrKlWpMJWnTNLP+LVHCwP+E8idSyr+S0umJlAunPmllr6mTiZKznZx1W8vVu1PXv/sqw7svbvThm4SvP/rLoRFTt5vGbnQOztY5SOh7GVy0FrpcY/le3mz/uLc/fsu7n39l/OmX9Lodub6+lpubG9WPYCytwuHd3t6qnvQu/cmz72HHWq5SLrmgxM+86HTgvZ+kZv8R823OTbfdneh2Pen+A1g2iIzGRdbnAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "iconography contribution exportsvgfromai",
            "title": "iconography contribution exportsvgfromai",
            "src": "/static/dcb7fdf20c1fea502cb07e889b4b1790/3cbba/iconography-contribution-exportsvgfromai.png",
            "srcSet": ["/static/dcb7fdf20c1fea502cb07e889b4b1790/7fc1e/iconography-contribution-exportsvgfromai.png 288w", "/static/dcb7fdf20c1fea502cb07e889b4b1790/a5df1/iconography-contribution-exportsvgfromai.png 576w", "/static/dcb7fdf20c1fea502cb07e889b4b1790/3cbba/iconography-contribution-exportsvgfromai.png 1152w", "/static/dcb7fdf20c1fea502cb07e889b4b1790/32b2a/iconography-contribution-exportsvgfromai.png 1190w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2>{`Contribution process`}</h2>
    <p>{`Does your pictogram have potential for other materials at IBM? If so, please consider contributing to the design system. IBM welcomes pictogram contributions in the form of a pull request to our pictogram repository (coming soon). In the mean time to make a pull request, please `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-icons/issues/new"
      }}>{`submit an issue in the repo`}</a>{` with the pictogram attached.`}</p>
    <p>{`Please note that Carbon contribution is `}<strong parentName="p">{`not required`}</strong>{` in order to introduce a new pictogram into your materials. If your pictogram is determined to be broadly useful in Carbon and passes IBM Brand design reviews, then it may also be integrated into the design system.`}</p>
    <p>{`If your submission is accepted, the team will notify you. If changes are needed, you’ll need to create a new contribution issue after reworking it based on feedback from the Design System and Brand teams.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      